import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { portalMe, clearPortalSession, getPortalToken, isPortalSessionExpired } from '@shared/services/portalService';
import { useToast } from '@shared/components/ui/use-toast';
import PortalHelpModal from '@/components/portal/PortalHelpModal';
import { cn } from '@shared/lib/utils';
import { useTheme } from '@shared/hooks/useTheme';
import { supabase } from '@shared/supabaseClient';
import { QRCodeSVG } from 'qrcode.react';
import {
  LayoutDashboard,
  BadgeCheck,
  FolderKanban,
  ShieldCheck,
  LogOut,
  HelpCircle,
  Sun,
  Moon,
  Menu,
  ClipboardList,
  Users,
  ArrowLeft,
  BookOpen,
  Lock,
} from 'lucide-react';

const navItems = [
  { to: '/portal/inductions',    label: 'Invites',      icon: ClipboardList },
  { to: '/portal/credentials',   label: 'Credentials',  icon: ShieldCheck },
];

const SectionHeading = ({ label, pt = false }) => (
  <div className={cn('pb-1 px-3 flex flex-col items-center gap-2', pt && 'pt-4')}>
    <div className="h-px w-full bg-primary/40" />
    <p className="text-xs font-semibold text-primary uppercase tracking-widest">{label}</p>
    <div className="h-px w-full bg-primary/40" />
  </div>
);

const PortalLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  const [logoSize, setLogoSize] = useState(80);
  const [showHelp, setShowHelp] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // navStatus drives the colored indicators on sidebar items
  // { inductions: 'action'|'pending'|'complete'|null, badge: 'active'|'pending'|null, inductionCount: number, projectCount: number }
  const [navStatus, setNavStatus] = useState({ badge: null, inductionCount: 0, projectCount: 0 });
  const [sidebarPasses, setSidebarPasses] = useState([]);
  // Incremented by child pages (via 'induction-status-changed' event) to force a re-check
  const [navRefreshKey, setNavRefreshKey] = useState(0);

  const isPortalEntry = location.pathname === '/portal';

  // Detect if user is inside a specific project context
  const projectRouteMatch = location.pathname.match(/^\/portal\/projects\/([a-f0-9-]{36}(?:-[a-f0-9]+)*)/);
  const routeProjectId = projectRouteMatch ? projectRouteMatch[1] : null;

  // Detect if user is inside a specific induction context
  const inductionRouteMatch = location.pathname.match(/^\/portal\/inductions\/([a-f0-9-]{36})/);
  const routeInductionId = inductionRouteMatch ? inductionRouteMatch[1] : null;
  const isInductionsList = location.pathname === '/portal/inductions' || location.pathname === '/portal/inductions/';
  const [contextInduction, setContextInduction] = useState(null);

  // Persist induction context across sub-pages (e.g. /portal/digital-badge).
  // Clear when user returns to the inductions list.
  useEffect(() => {
    if (routeInductionId) {
      sessionStorage.setItem('portalActiveInductionId', routeInductionId);
    } else if (isInductionsList) {
      sessionStorage.removeItem('portalActiveInductionId');
    }
  }, [routeInductionId, isInductionsList]);

  const contextInductionId = routeInductionId || (!isInductionsList ? (sessionStorage.getItem('portalActiveInductionId') || null) : null);

  // Persist project context across non-project sub-pages (e.g. /portal/inductions, /portal/profile).
  // Only clear when user explicitly returns to the projects list.
  const isProjectsList = location.pathname === '/portal/projects' || location.pathname === '/portal/projects/';
  useEffect(() => {
    if (routeProjectId) {
      sessionStorage.setItem('portalActiveProjectId', routeProjectId);
    } else if (isProjectsList) {
      sessionStorage.removeItem('portalActiveProjectId');
    }
  }, [routeProjectId, isProjectsList]);

  const contextProjectId = routeProjectId || (!isProjectsList ? (sessionStorage.getItem('portalActiveProjectId') || null) : null);
  const [contextProject, setContextProject] = useState(null);

  // Load project name when context changes
  useEffect(() => {
    if (!contextProjectId) { setContextProject(null); return; }
    supabase
      .from('projects')
      .select('id, project_name, project_lead_email, status')
      .eq('id', contextProjectId)
      .maybeSingle()
      .then(({ data }) => setContextProject(data || null))
      .catch(() => setContextProject(null));
  }, [contextProjectId]);

  const isLeadForContextProject =
    session?.person?.email &&
    contextProject?.project_lead_email?.toLowerCase().trim() ===
      session.person.email.toLowerCase().trim();

  // Load induction title when induction context changes
  useEffect(() => {
    if (!contextInductionId || !session?.person?.email) { setContextInduction(null); return; }
    supabase
      .rpc('portal_get_briefs_by_email', { p_email: session.person.email })
      .then(({ data }) => {
        const match = (data || []).find(r => r.submission_id === contextInductionId);
        setContextInduction(match ? { submission_id: match.submission_id, title: match.title, depot_name: match.depot_name, status: match.status } : null);
      })
      .catch(() => setContextInduction(null));
  }, [contextInductionId, session?.person?.email]);

  const displayName = useMemo(() => {
    if (!session?.person?.name && !session?.person?.email) return 'Supplier Portal';
    return session?.person?.name || session?.person?.email;
  }, [session]);

  // Load session once on mount — never repeat on navigation
  useEffect(() => {
    if (isPortalEntry) return;

    const token = getPortalToken();
    if (!token || isPortalSessionExpired()) {
      clearPortalSession();
      navigate('/portal', { replace: true });
      return;
    }

    // Already have a valid in-memory session — nothing to do
    if (session) return;

    setLoading(true);
    portalMe(token)
      .then((data) => {
        if (!data?.ok) throw new Error('Unauthorized');
        setSession(data);
      })
      .catch(() => {
        clearPortalSession();
        toast({
          title: 'Session expired',
          description: 'Please request a new portal code.',
          variant: 'destructive',
        });
        navigate('/portal', { replace: true });
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortalEntry]); // intentionally omit location.pathname — session is loaded once

  // On every nav: only do the cheap synchronous expiry guard (no spinner, no API call)
  useEffect(() => {
    if (isPortalEntry || !session) return;
    if (isPortalSessionExpired()) {
      clearPortalSession();
      navigate('/portal', { replace: true });
    }
  }, [isPortalEntry, location.pathname, session, navigate]);

  // Once session is available, load induction status for sidebar indicators
  useEffect(() => {
    if (!session?.person?.email) return;
    const email = session.person.email;

    Promise.all([
      supabase.rpc('portal_get_induction_summary_by_email', { p_email: email }),
      supabase.from('passes').select('id, status').ilike('person_email', email).eq('status', 'active'),
      // Fetch projects where user is lead — count those not yet approved/completed
      Promise.resolve(supabase.from('projects')
        .select('id, status')
        .ilike('project_lead_email', email)
        .not('status', 'in', '("approved","completed","archived")')
      ).catch(() => ({ data: null })),
      // Fetch team memberships with project status to check if project itself needs action
      Promise.resolve(supabase.from('project_team_members')
        .select('project_id, status, projects!inner(status)')
        .ilike('person_email', email)
      ).catch(() => ({ data: null })),
    ]).then(([{ data }, { data: passes }, { data: leadProjects }, { data: teamRows }]) => {
        // ── Induction indicators ───────────────────────────────────────
        // RPC now returns all outstanding individually + latest approved per brief
        // so no client-side dedup is needed.
        // Exclude team-member-linked submissions (TM- prefix) — those belong to
        // the Projects flow and must not inflate the Invites badge count.
        const rows = (data || []).filter(r => !r.public_reference?.startsWith('TM-'));
        const statuses = rows.map(r => r.status);
        // Outstanding = anything not yet approved (invited, in_progress, pending_acknowledgement, submitted)
        const outstandingCount = statuses.filter(s => s !== 'approved').length;

        const hasActivePass = Array.isArray(passes) && passes.some(p => p.status === 'active');
        const hasApproved = statuses.some(s => s === 'approved');
        const hasPending  = statuses.some(s => s === 'submitted');
        const badgeStatus   = hasActivePass ? 'active'
                            : (hasApproved || hasPending) ? 'pending'
                            : null;

        // ── Project count: how many projects are yet to be completed ─
        let projectActionCount = 0;
        const countedProjectIds = new Set();

        // Lead projects that are not completed/archived
        if (Array.isArray(leadProjects)) {
          leadProjects.forEach(p => {
            countedProjectIds.add(p.id);
            projectActionCount++;
          });
        }

        // Team memberships: only count if the project itself isn't approved/completed/archived
        if (Array.isArray(teamRows)) {
          const doneStatuses = ['approved', 'completed', 'archived'];
          const needsAction = teamRows.filter(r => {
            const projStatus = r.projects?.status;
            return projStatus && !doneStatuses.includes(projStatus);
          });
          needsAction.forEach(r => {
            if (!countedProjectIds.has(r.project_id)) {
              countedProjectIds.add(r.project_id);
              projectActionCount++;
            }
          });
        }

        setNavStatus({
          badge:      badgeStatus,
          inductionCount: outstandingCount,
          projectCount: projectActionCount,
        });
      })
      .catch(() => { /* non-critical — indicators simply won't show */ });
  // Re-run when email changes or when a child page fires 'induction-status-changed'
  }, [session?.person?.email, navRefreshKey]);

  // Re-check nav indicators when a child page signals a status change
  useEffect(() => {
    const handler = () => setNavRefreshKey(k => k + 1);
    window.addEventListener('induction-status-changed', handler);
    return () => window.removeEventListener('induction-status-changed', handler);
  }, []);

  // Load full pass details for sidebar QR display
  useEffect(() => {
    if (!session?.person?.email) return;
    supabase
      .from('passes')
      .select('id, pass_token, status, valid_from, valid_until, depot_id, depots:depot_id(name)')
      .ilike('person_email', session.person.email)
      .in('status', ['active', 'suspended'])
      .then(({ data }) => {
        if (!data) return;
        // Deduplicate — keep one pass per depot (prefer active over suspended)
        const seen = new Map();
        data.forEach(p => {
          const key = p.depot_id || 'global';
          if (!seen.has(key) || p.status === 'active') seen.set(key, p);
        });
        setSidebarPasses([...seen.values()]);
      })
      .catch(() => {});
  }, [session?.person?.email, navRefreshKey]);

  // Load company logo + size from admin settings (shared via localStorage)
  useEffect(() => {
    const loadBranding = () => {
      const saved = localStorage.getItem('company_logo_url');
      if (saved && saved !== 'null') {
        setCompanyLogoUrl(saved);
      }
      const savedSize = parseInt(localStorage.getItem('company_logo_size') || '80');
      setLogoSize(savedSize);
    };
    loadBranding();

    // Listen for admin settings changes (real-time sync)
    const handleSettingsUpdate = () => loadBranding();
    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('settingsUpdated', handleSettingsUpdate);
  }, []);

  const { theme, toggleTheme } = useTheme();

  const handleLogout = () => {
    clearPortalSession();
    navigate('/portal', { replace: true });
  };

  const fmtDate = React.useCallback((d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }, []);

  const SidebarContent = useMemo(() => (
    <>
      {/* Logo */}
      <div className="flex flex-col items-center justify-center px-4 pt-4 pb-3 border-b border-border">
        <img
          src="/assets/Atlaslogo.png"
          alt="Atlas Logo"
          className="w-full h-auto max-h-32 object-contain"
        />
        <div className="mt-3 flex flex-col items-center gap-1">
          <div className="h-px w-full bg-primary/40" />
          <p className="text-xs font-semibold text-primary uppercase tracking-widest py-1">Site Visitor Portal</p>
          <div className="h-px w-full bg-primary/40" />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">

        {/* ── Dashboard — always visible ─────────────────────────── */}
        <NavLink
          to="/portal/dashboard"
          onClick={() => setSidebarOpen(false)}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
              isActive
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
            )
          }
        >
          <LayoutDashboard className="w-4 h-4 shrink-0" />
          <span className="flex-1">Dashboard</span>
        </NavLink>

        <>
          {navItems.map((item) => {
            const Icon = item.icon;
            const indicator = (() => {
              if (item.to === '/portal/inductions') {
                const total = navStatus.inductionCount + navStatus.projectCount;
                if (total > 0) return { color: 'bg-amber-400', title: 'Outstanding invites & projects', count: total };
              }
              if (item.to === '/portal/digital-badge') {
                if (navStatus.badge === 'active')  return { color: 'bg-green-500', title: 'Badge active' };
                if (navStatus.badge === 'pending') return { color: 'bg-amber-400', title: 'Awaiting approval' };
              }
              return null;
            })();
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : item.gold
                        ? 'text-primary hover:bg-secondary hover:text-primary'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                  )
                }
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="flex-1">{item.label}</span>
                {indicator && (
                  indicator.count > 0
                    ? <span title={indicator.title} className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold text-black rounded-full shrink-0 ${indicator.color}`}>{indicator.count}</span>
                    : <span title={indicator.title} className={`w-2.5 h-2.5 rounded-full shrink-0 ${indicator.color}`} />
                )}
              </NavLink>
            );
          })}
        </>

        {/* ── Digital Pass ── */}
        {sidebarPasses.length === 0 ? (
          <div className="px-3 pb-2">
            <NavLink
              to="/portal/digital-badge"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-semibold transition-colors w-full',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-primary/10 text-primary hover:bg-primary/20',
                )
              }
            >
              <BadgeCheck className="w-3.5 h-3.5 shrink-0" />
              My Digital Pass
            </NavLink>
          </div>
        ) : (
          <div className="px-3 pb-2 pt-4 space-y-3">
            {sidebarPasses.map((pass) => {
              const suspended = pass.status === 'suspended';
              return (
                <NavLink
                  key={pass.id}
                  to="/portal/digital-badge"
                  onClick={() => setSidebarOpen(false)}
                  className="block group cursor-pointer"
                >
                  {/* Site name — centred */}
                  <p className="text-xs font-bold text-foreground text-center mb-2 truncate px-1">
                    {pass.depots?.name || 'Site Pass'}
                  </p>

                  {/* QR code */}
                  <div className="flex justify-center group-hover:opacity-75 transition-opacity mb-3">
                    {suspended ? (
                      <div className="w-[156px] h-[156px] rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center">
                        <Lock className="h-10 w-10 text-amber-400" />
                      </div>
                    ) : (
                      <div className="bg-white p-2 rounded-md shadow-sm">
                        <QRCodeSVG value={`APEX:${pass.pass_token}`} size={156} />
                      </div>
                    )}
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-1 text-center mb-2 px-1">
                    <div>
                      <p className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">From</p>
                      <p className="text-[11px] font-medium text-foreground">{fmtDate(pass.valid_from)}</p>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">Until</p>
                      <p className="text-[11px] font-medium text-foreground">{fmtDate(pass.valid_until)}</p>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex justify-center">
                    {suspended ? (
                      <span className="text-[10px] font-bold text-amber-600 bg-amber-100/60 border border-amber-300/50 rounded-full px-3 py-0.5">Suspended</span>
                    ) : (
                      <span className="text-[10px] font-bold text-green-600 bg-green-100/20 border border-green-500/40 rounded-full px-3 py-0.5">Active</span>
                    )}
                  </div>
                </NavLink>
              );
            })}
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="px-3 py-4 border-t border-border space-y-1">
        <button
          onClick={toggleTheme}
          className="flex items-center justify-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
        >
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
        </button>
        <button
          onClick={handleLogout}
          className="flex items-center justify-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
        <p className="px-3 text-xs text-muted-foreground truncate text-center pb-1">{displayName}</p>
      </div>
    </>
  ), [contextProjectId, contextInductionId, contextProject, contextInduction, isLeadForContextProject, navStatus, sidebarOpen, theme, displayName, navigate, toggleTheme, sidebarPasses, fmtDate]);

  if (isPortalEntry) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-background portal-shell">
      <div className="flex min-h-screen">
        {/* ── Mobile sidebar drawer (overlay) ── */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="dark relative w-full h-full flex flex-col bg-card border-r border-border z-10 overflow-y-auto">
              {SidebarContent}
            </aside>
          </div>
        )}

        {/* ── Desktop sidebar (persistent) ── */}
        <aside className="dark hidden lg:flex fixed left-0 top-0 w-[375px] h-screen flex-col bg-card border-r border-border z-30">
          {SidebarContent}
        </aside>

        <main className="flex-1 min-w-0 overflow-x-hidden lg:ml-[375px] bg-gradient-to-b from-background via-background to-muted/30">
          {/* Top bar — matches starter app style */}
          <div className="dark shrink-0 border-b border-border bg-card px-4 py-2">
            <div className="relative flex items-center min-h-[44px]">
              {/* Hamburger — mobile only, left */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-1.5 rounded hover:bg-secondary transition-colors flex-shrink-0"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5 text-foreground" />
              </button>

              {/* Org logo — centered absolutely */}
              {companyLogoUrl && (
                <div className="absolute left-1/2 -translate-x-1/2">
                  <img
                    src={companyLogoUrl}
                    alt="Company Logo"
                    style={{ height: `${Math.min(Math.max(logoSize || 46, 32), 40)}px`, width: 'auto', objectFit: 'contain' }}
                  />
                </div>
              )}

              {/* User name + help — right */}
              <div className="ml-auto flex items-center gap-3">
                <div className="hidden sm:flex flex-col items-end max-w-[220px]">
                  <span className="text-base font-semibold text-foreground truncate leading-tight">{displayName}</span>
                  {session?.person?.company && (
                    <span className="text-xs text-muted-foreground truncate leading-tight">{session.person.company}</span>
                  )}
                </div>
                {contextProjectId && (
                  <span className={`hidden sm:inline-block text-xs font-bold px-2 py-0.5 rounded-full border ${
                    isLeadForContextProject
                      ? 'bg-primary/15 text-primary border-primary/30'
                      : 'bg-muted text-muted-foreground border-border'
                  }`}>
                    {isLeadForContextProject ? 'Project Lead' : 'Team Member'}
                  </span>
                )}
                <button
                  onClick={() => setShowHelp(true)}
                  className="p-1.5 rounded hover:bg-secondary transition-colors"
                  title="Help & User Guide"
                >
                  <HelpCircle className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            </div>
          </div>

          <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 lg:px-8 overflow-x-hidden min-w-0">
            {loading ? (
              <div className="rounded-xl border border-border bg-card p-6 text-center text-muted-foreground">
                Loading portal session...
              </div>
            ) : (
              <Outlet context={{ session, contextProjectId, contextInductionId, contextInduction: contextInductionId ? contextInduction : null }} />
            )}
          </div>
        </main>
      </div>

      {/* Help & User Guide Modal */}
      <PortalHelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />
    </div>
  );
};

export default PortalLayout;

